// © Microsoft Corporation. All rights reserved.

import { AppError, ResolverEnvironment } from "@msnews/core-contracts";
import { ConfigCachingConstants, ResolverAppErrors, getCacheKeyFromConfigRef } from "../../utilities";
import { ConfigReference, ExperienceConfig, ExperienceConfigCachingWrapper } from "@msnews/config-data-types";
import { IndexedDb, createError } from "@msnews/web-worker-shared-utils";

import { memoize } from "lodash-es";

/**
 * Type definition for cached configs in the indexed DB
 */
type CachedConfigsType = Map<string, ExperienceConfigCachingWrapper>;

/**
 * Config cache class
 * This interacts with browser's storage and get/set resolved and merged config(s).
 */
export class ConfigCache {
    /**
     * True if config caching is enabled else false.
     */
    public enableConfigCaching: boolean = this.useConfigCaching();

    /**
     * Instance of IndexedDb class
     */
    private indexedDb: IndexedDb;

    /**
     * Resolver environment properties
     */
    private resolverEnvironment: ResolverEnvironment;

    /**
     * Current Timestamp for config staleness check
     */
    private stalenessCheckTimestamp: number = new Date().getTime();

    /**
     * In-memory map storing configs before read/write to cache.
     */
    private indexedDbMap: Map<string, ExperienceConfigCachingWrapper> = new Map();

    /**
     * True if indexedDB is already initialized
     */
    private indexedDbInitialized: boolean;

    /**
     * Read indexedDb for configs.
     */
    private readConfigsFromIdb = memoize(this._readConfigsFromIdb);

    /**
     * Function checks if ConfigCaching is enabled.
     * @returns true if supported else false.
     */
    private useConfigCaching(): boolean {

        // Dev scenario to enable/disable config service
        const queryParams = this.resolverEnvironment
            && this.resolverEnvironment.configServiceParameters
            && this.resolverEnvironment.queryParams;

        if (queryParams && queryParams["enableConfigCaching"]) {
            return queryParams["enableConfigCaching"] === "true";
        }

        // Enable config caching by default
        return true;
    }

    /**
     * Config resolver constructor to initialize child classes.
     * @param {ResolverEnvironment} resolverEnvironment
     */
    constructor(readonly environment: ResolverEnvironment, enableIdbCaching = false) {

        if (!environment
            || !environment.targetScope
            || !environment.appType) {
            throw new Error("ResolverEnvironment or targetScope or appType does not exist.");
        }

        // Memoize the indexedDB function to protect from being called again
        this.initializeIdb = memoize(this.initializeIdb);

        this.resolverEnvironment = environment;
        this.indexedDb = new IndexedDb();

        // Do not need to init IDB if IDB caching is disabled
        if (enableIdbCaching) {
            this.initializeIdb();
        }
    }

    /**
     * Fast helper to try to get it directly from cache so callers do not have to go through
     * additional async layers to get to the result if we've already loaded up the memory map.
     * @param configRef - The config ref to get from memory cache.
     */
    public tryGetConfigSync(configRef: ConfigReference): ExperienceConfig | undefined {
        if (configRef) {
            const cacheKey = getCacheKeyFromConfigRef(configRef, this.resolverEnvironment.appType);
            return this.indexedDbMap.get(cacheKey);
        }
    }

    /**
     * This function returns resolve and merge config from browser cache.
     * @param {ConfigReference} configRef: The identity structure that uniquely identifies a single instance of an experience
     * @returns {Promise<ExperienceConfig>} experienceConfig from cache
     */
    public async getConfig(configRef: ConfigReference): Promise<ExperienceConfig> {
        if (!configRef) {
            return;
        }

        const cacheKey = getCacheKeyFromConfigRef(configRef, this.resolverEnvironment.appType);

        // If this config exist in memory, return else read from cache.
        if (!this.indexedDbMap.has(cacheKey)) {
            await this.readConfigsFromIdb();
        }

        return this.indexedDbMap.get(cacheKey);
    }

    /**
     * Update IndexedDB configs from network configs.
     * @param configMap
     */
    public async updateCache(configMap: Map<string, ExperienceConfig>): Promise<void> {
        if (!configMap) {
            return;
        }

        configMap.forEach((experienceConfig, cacheKey) => {
            if (experienceConfig) {
                const config: ExperienceConfigCachingWrapper = {
                    storageTimestamp: this.stalenessCheckTimestamp,
                    configRefCacheKey: cacheKey,
                    properties: experienceConfig.properties
                };
                this.indexedDbMap.set(cacheKey, config);
            }
        });

        await this.writeConfigMapToIdb();
    }

    /**
     * Init indexedDb.
     */
    private async initializeIdb(): Promise<void> {
        if (!this.enableConfigCaching) {
            return;
        }

        try {

            const idbConfigOpenPromise = this.resolverEnvironment.configCacheProperties && this.resolverEnvironment.configCacheProperties.idbConfigOpenPromise;
            await this.indexedDb.initializeIndexedDb(1, ConfigCachingConstants.idbStoreName, ConfigCachingConstants.idbName, idbConfigOpenPromise);
            this.indexedDbInitialized = true;
            this.logMessage("IndexedDB initialized.");

        } catch (e) {
            this.enableConfigCaching = false;
            this.sendAppError(createError(
                ResolverAppErrors.IndexedDbInitFailed,
                "Failed to init configs IndexedDB",
                e
            ));
        }
    }

    /**
     * Get cached Configs from IndexedDb and populate configMap based on target scope key.
     */
    private async _readConfigsFromIdb(): Promise<void> {
        if (!this.enableConfigCaching) {
            return;
        }

        // Check if IndexedDB was initialized
        if (!this.indexedDbInitialized) {
            await this.initializeIdb();
        }

        try {
            // This is indexedDB cache key and all configs are stored as a value with this key.
            const indexedDbKey = this.generateIdbKey();
            let cachedConfigs: CachedConfigsType;
            let cacheSource: string;

            const { idbConfigLookup = null, idbConfigReadPromise = null } = this.resolverEnvironment.configCacheProperties || {};

            // Check if we have a top page-level initialized IDB read config lookup populated
            // with the specific entry we need, and if so use it. Otherwise proceed to load from indexDB
            cachedConfigs = (idbConfigLookup || {})[indexedDbKey] as CachedConfigsType;
            if (cachedConfigs) {
                cacheSource = "pre-initialized config cache";

            } else if (idbConfigReadPromise) {
                // see if we have the pre-initialized config read promise from IDB, and if so
                // await on that instead of starting our own duplicate IDB init/read.
                cacheSource = "pre-initialized IndexedDb read";
                await idbConfigReadPromise;
                cachedConfigs = (idbConfigLookup || {})[indexedDbKey] as CachedConfigsType;
            } else {
                // start our own IDB read.
                cacheSource = "IndexedDb bulk read";
                cachedConfigs = await this.indexedDb.getObject<Map<string, ExperienceConfigCachingWrapper>>(indexedDbKey);
            }

            if (!cachedConfigs) {
                this.logMessage(`readConfigsFromIdb: Didn't find any config in IndexedDB. CacheSource: ${cacheSource}`);
            } else {
                // targetScopeKey is inserted into IndexedDb along with configIndexHref.
                // Although there can be more than type of keys being formed since DHP and NTP will have different ones due to pageType in targetScope.
                cachedConfigs.forEach(cachedConfig => {
                    if (cachedConfig.configRefCacheKey
                        && !this.isConfigStale(cachedConfig.storageTimestamp)) {
                        this.indexedDbMap.set(cachedConfig.configRefCacheKey, cachedConfig);
                    }
                });

                this.logMessage(`readConfigsFromIdb: Configs in IndexedDB: ${cachedConfigs.size}. Configs in memory: ${this.indexedDbMap.size}. CacheSource: ${cacheSource}.`);
            }
        } catch (e) {
            this.sendAppError(createError(
                ResolverAppErrors.IndexedDbReadFailed,
                "IndexedDb Configs read failed",
                e
            ));
        }
    }

    /**
     * Write in-memory configs to indexedDb.
     */
    private async writeConfigMapToIdb(): Promise<void> {
        if (!this.enableConfigCaching || !this.indexedDbMap) {
            return;
        }

        // Check if IndexedDB was initialized
        if (!this.indexedDbInitialized) {
            await this.initializeIdb();
        }

        try {
            // This is indexedDB cache key and all configs are stored as a value with this key.
            const indexedDbKey = this.generateIdbKey();
            await this.indexedDb.setObject(indexedDbKey, this.indexedDbMap);
            this.logMessage("writeConfigMapToIdb: completed");

        } catch (e) {
            this.sendAppError(createError(
                ResolverAppErrors.IndexedDbWriteFailed,
                "Failed to write configMap to indexedDb",
                e
            ));
        }
    }

    /**
     * This function generates a key that is used to write/read configurations to/from storage.
     * example key: "kids|ja|jp|testPage|mmx"
     * @private
     * @return string
     */
    private generateIdbKey(): string {
        const { audienceMode = "", locale = {}, pageType = "" } = this.resolverEnvironment.targetScope || {};
        const { language, market } = locale;

        const keys = [audienceMode, language, market, pageType, this.resolverEnvironment.appType || ""];
        return keys.map(key => key || "").join("|").toLowerCase();
    }

    /**
     * Function to check config is stale.
     * @returns true if stale else false.
     */
    private isConfigStale(storageTimestamp: number): boolean {
        return !storageTimestamp || (this.stalenessCheckTimestamp - storageTimestamp) > ConfigCachingConstants.configStalenessDuration;
    }

    /**
     * Send this app error
     * @param appError AppError to send to telemetry
     */
    private sendAppError(appError: AppError): void {
        if (this.resolverEnvironment.appErrorCallback) {
            this.resolverEnvironment.appErrorCallback(appError);
        }
    }

    /**
     * Log message if debug is chosen
     * @param message to log
     */
    private logMessage(message: string): void {
        if (this.resolverEnvironment.loggerCallback) {
            this.resolverEnvironment.loggerCallback(message);
        }
    }
}